import type {
    MediaAudioDeviceConstraints,
    MediaAudioInputDevice,
    MediaAudioInputQuery,
    MediaDeepReadonly,
    MediaDeviceQuery,
    MediaOutputDevice,
    MediaVideoDeviceConstraints,
    MediaVideoInputDevice,
    MediaVideoInputQuery,
} from '@yandex-360/telemost-sdk/device-manager';
import { isMediaInputDevice } from '@yandex-360/telemost-sdk/device-manager';
import type {
    ActiveDevices,
    Device,
    DeviceOrQuery,
    InputDevice,
    OutputDevice,
    SelectedDevices,
} from '@yandex-video-platform/goloom-sdk';

type GoloomAudioInputDevice = NonNullable<ActiveDevices['audioInput']>;
type GoloomVideoInputDevice = NonNullable<ActiveDevices['videoInput']>;
type GoloomAudioInputSelection = NonNullable<SelectedDevices['audioInput']>;
type GoloomVideoInputSelection = NonNullable<SelectedDevices['videoInput']>;
type GoloomAudioInputQuery = Exclude<GoloomAudioInputSelection, GoloomAudioInputDevice>;
type GoloomVideoInputQuery = Exclude<GoloomVideoInputSelection, GoloomVideoInputDevice>;
type GoloomAudioDeviceConstraints = GoloomAudioInputDevice['constraints'];
type GoloomVideoDeviceConstraints = GoloomVideoInputDevice['constraints'];

const mapAudioDeviceConstraintsFromGoloom = (
    constraints: GoloomAudioDeviceConstraints
): MediaDeepReadonly<MediaAudioDeviceConstraints> => ({
    echoCancellation: constraints.echoCancellation,
    autoGainControl: constraints.autoGainControl,
    channelCount: constraints.channelCount,
    latency: constraints.latency,
    noiseSuppression: constraints.noiseSuppression,
    sampleRate: constraints.sampleRate,
    sampleSize: constraints.sampleSize,
    suppressLocalAudioPlayback: constraints.suppressLocalAudioPlayback,
});

const mapVideoDeviceConstraintsFromGoloom = (
    constraints: GoloomVideoDeviceConstraints
): MediaDeepReadonly<MediaVideoDeviceConstraints> => ({
    aspectRatio: constraints.aspectRatio,
    facingMode: constraints.facingMode,
    frameRate: constraints.frameRate,
    height: constraints.height,
    width: constraints.width,
});

const mapAudioDeviceConstraintsToGoloom = (
    constraints?: MediaDeepReadonly<MediaAudioDeviceConstraints> | MediaAudioDeviceConstraints
): Maybe<GoloomAudioDeviceConstraints> => {
    if (!constraints) {
        return undefined;
    }

    return {
        echoCancellation: constraints.echoCancellation,
        autoGainControl: constraints.autoGainControl,
        channelCount: constraints.channelCount,
        latency: constraints.latency,
        noiseSuppression: constraints.noiseSuppression,
        sampleRate: constraints.sampleRate,
        sampleSize: constraints.sampleSize,
        suppressLocalAudioPlayback: constraints.suppressLocalAudioPlayback,
    };
};

const mapVideoDeviceConstraintsToGoloom = (
    constraints?: MediaDeepReadonly<MediaVideoDeviceConstraints> | MediaVideoDeviceConstraints
): Maybe<GoloomVideoDeviceConstraints> => {
    if (!constraints) {
        return undefined;
    }

    return {
        aspectRatio: constraints.aspectRatio,
        facingMode: constraints.facingMode,
        frameRate: constraints.frameRate,
        height: constraints.height,
        width: constraints.width,
    };
};

const isGoloomInputDevice = (device: Device | DeviceOrQuery): device is InputDevice =>
    device.kind !== 'audiooutput' && 'track' in device;

const isGoloomOutputDevice = (device: Device | DeviceOrQuery): device is OutputDevice => device.kind === 'audiooutput';

export const mapAudioInputDeviceFromGoloom = (device: GoloomAudioInputDevice): MediaAudioInputDevice => ({
    kind: 'audioinput',
    id: device.id,
    label: device.label,
    group: device.group,
    track: device.track,
    constraints: mapAudioDeviceConstraintsFromGoloom(device.constraints),
    activeConstraints: mapAudioDeviceConstraintsFromGoloom(device.activeConstraints),
});

export const mapVideoInputDeviceFromGoloom = (device: GoloomVideoInputDevice): MediaVideoInputDevice => ({
    kind: 'videoinput',
    id: device.id,
    label: device.label,
    group: device.group,
    track: device.track,
    constraints: mapVideoDeviceConstraintsFromGoloom(device.constraints),
    activeConstraints: mapVideoDeviceConstraintsFromGoloom(device.activeConstraints),
});

export const mapOutputDeviceFromGoloom = (device: OutputDevice): MediaOutputDevice => ({
    kind: 'audiooutput',
    id: device.id,
    label: device.label,
    group: device.group,
    constraints: device.constraints ? mapVideoDeviceConstraintsFromGoloom(device.constraints) : undefined,
    activeConstraints: device.activeConstraints
        ? mapVideoDeviceConstraintsFromGoloom(device.activeConstraints)
        : undefined,
});

export const mapAudioInputQueryFromGoloom = (device: GoloomAudioInputQuery): MediaAudioInputQuery => ({
    kind: 'audioinput',
    constraints: device.constraints ? mapAudioDeviceConstraintsFromGoloom(device.constraints) : undefined,
});

export const mapVideoInputQueryFromGoloom = (device: GoloomVideoInputQuery): MediaVideoInputQuery => ({
    kind: 'videoinput',
    constraints: device.constraints ? mapVideoDeviceConstraintsFromGoloom(device.constraints) : undefined,
});

export const mapDeviceFromGoloom = (device: Device | DeviceOrQuery): MediaDeviceQuery => {
    if (isGoloomInputDevice(device)) {
        return device.kind === 'audioinput'
            ? mapAudioInputDeviceFromGoloom(device)
            : mapVideoInputDeviceFromGoloom(device);
    }

    if (isGoloomOutputDevice(device)) {
        return mapOutputDeviceFromGoloom(device);
    }

    return device.kind === 'audioinput' ? mapAudioInputQueryFromGoloom(device) : mapVideoInputQueryFromGoloom(device);
};

export const mapAudioInputDeviceToGoloom = (device: MediaAudioInputDevice): GoloomAudioInputDevice => ({
    id: device.id,
    kind: 'audioinput',
    label: device.label,
    group: device.group,
    track: device.track,
    constraints: mapAudioDeviceConstraintsToGoloom(device.constraints) ?? {},
    activeConstraints: mapAudioDeviceConstraintsToGoloom(device.activeConstraints) ?? {},
});

export const mapVideoInputDeviceToGoloom = (device: MediaVideoInputDevice): GoloomVideoInputDevice => ({
    id: device.id,
    kind: 'videoinput',
    label: device.label,
    group: device.group,
    track: device.track,
    constraints: mapVideoDeviceConstraintsToGoloom(device.constraints) ?? {},
    activeConstraints: mapVideoDeviceConstraintsToGoloom(device.activeConstraints) ?? {},
});

export const mapOutputDeviceToGoloom = (device: MediaOutputDevice): OutputDevice => ({
    id: device.id,
    kind: 'audiooutput',
    label: device.label,
    group: device.group,
    constraints: mapVideoDeviceConstraintsToGoloom(device.constraints),
    activeConstraints: mapVideoDeviceConstraintsToGoloom(device.activeConstraints),
});

export const mapAudioInputQueryToGoloom = (device: MediaAudioInputQuery): GoloomAudioInputQuery => ({
    kind: 'audioinput',
    constraints: mapAudioDeviceConstraintsToGoloom(device.constraints),
});

export const mapVideoInputQueryToGoloom = (device: MediaVideoInputQuery): GoloomVideoInputQuery => ({
    kind: 'videoinput',
    constraints: mapVideoDeviceConstraintsToGoloom(device.constraints),
});

export const mapDeviceToGoloom = (device: MediaDeviceQuery): DeviceOrQuery => {
    if (isMediaInputDevice(device)) {
        return device.kind === 'audioinput' ? mapAudioInputDeviceToGoloom(device) : mapVideoInputDeviceToGoloom(device);
    }

    if (device.kind === 'audiooutput') {
        return mapOutputDeviceToGoloom(device);
    }

    return device.kind === 'audioinput' ? mapAudioInputQueryToGoloom(device) : mapVideoInputQueryToGoloom(device);
};
